// Alur PBJ (Non-Penyedia, feature pbj_flow): the 17 diagrams of the PBJ
// cycle from data/pbj/pbj-seed.json, browsed as lists, not drawn.
//   header    plain spacing      h1, one sentence
//   toolbar   divided section    Diagram (grouped: Tahap utama, Metode
//                                pemilihan, Swakelola, Diagram menyeluruh),
//                                Project (the PBJ project whose ticks show
//                                as done, reference flow canvas; "Tanpa
//                                project" shows none) and the "Hanya
//                                aktivitas yang dapat dicentang peran saya"
//                                switch
//   diagram   region             the goal, the project's progress on this
//                                diagram, then the nodes in order: number,
//                                kegiatan, pelaku, Selesai for a node ticked
//                                in the project, Keputusan for a decision,
//                                Peran Anda on the current role's own, Di
//                                luar Pluto on a step of another party; one
//                                selected
//   detail    region             the selected node: type, done by whom and
//                                when, pelaku, penjelasan, output, otoritas,
//                                simpul selanjutnya named by activity with
//                                the decision's branch labels, dasar hukum
//                                with JDIH links; Buat dokumen for a node
//                                with an output (a chat draft) and Unduh
//                                .docx (the Word skeleton); then the
//                                diagram's decision points
//   note      tonal              the diagram is an operational synthesis
// The diagram, project, node and filter live in the query
// (?diagram=&project=&node=&mine=1), so a link opens the same view; the PBJ
// project page's "Lihat alur" sets project. From 1024px the list and the
// detail sit side by side.
import { useId, useMemo } from "react";
import * as API from "../api";
import type { PbjActivity } from "../api";
import { useUser } from "../auth";
import { formatDateTime } from "../lib/format";
import { ROLE_LABEL } from "../lib/labels";
import { DIAGRAM_GROUP_LABEL, DIAGRAM_GROUPS, NODE_TYPE_LABEL, OUTSIDE_PLUTO_HINT } from "../pbj/labels";
import { canDocx, useDocx } from "../pbj/docx";
import { canDraft, useDraft } from "../pbj/drafts";
import { NodeFacts } from "../pbj/NodeFacts";
import { setQuery, useLocation } from "../router";
import { useFeatures } from "../shell/features";
import { projectPath } from "../shell/Projects";
import { Glyph } from "../ui/icons";
import { Badge, EmptyState, Notice, PageHeader, SectionHead, SkeletonRows, Switch, useAsync, useTitle } from "../ui/components";

const FIRST_DIAGRAM = "01_RENCANA";

export function AlurPbjScreen() {
  useTitle("Alur PBJ");
  const user = useUser();
  const { query } = useLocation();
  const switchId = useId();
  const features = useFeatures();
  const projectsOn = features.has("projects");
  const draft = useDraft();
  const docx = useDocx();
  const flow = useAsync(() => API.getPbjFlow(), [user.role]);
  const code = query.get("diagram") || FIRST_DIAGRAM;
  const mineOnly = query.get("mine") === "1";
  const projectId = projectsOn ? query.get("project") || null : null;
  const diagram = useAsync(() => API.getPbjDiagram(code), [code, user.role]);
  const pbjProjects = useAsync(() => (projectsOn ? API.listPbjProjects() : Promise.resolve([])), [projectsOn]);
  const project = useAsync(() => (projectId ? API.getPbjProject(projectId) : Promise.resolve(null)), [projectId]);

  // The project's activities by node id: done ones show as Selesai.
  const activities = useMemo(() => {
    const map = new Map<string, PbjActivity>();
    for (const s of project.data?.stages ?? []) for (const a of s.activities) map.set(a.id, a);
    return map;
  }, [project.data]);

  const nodes = diagram.data?.nodes ?? [];
  const shown = mineOnly ? nodes.filter((n) => n.mine) : nodes;
  const selected = shown.find((n) => n.id === query.get("node")) ?? shown[0] ?? null;
  const summaries = flow.data?.diagrams ?? [];
  const names = useMemo(() => new Map(nodes.map((n) => [n.id, `${n.order}. ${n.activity}`])), [nodes]);
  const projectName = project.data?.project.name ?? pbjProjects.data?.find((p) => p.id === projectId)?.name ?? null;
  // "Di luar Pluto" activities are never ticked, so they never count.
  const onRoute = nodes.filter((n) => activities.has(n.id) && !activities.get(n.id)!.outside);
  const doneHere = onRoute.filter((n) => activities.get(n.id)!.done).length;
  const selectedActivity = selected ? activities.get(selected.id) : undefined;

  return (
    <>
      <PageHeader divided title="Alur PBJ" lead="Siklus pengadaan barang/jasa pemerintah, melalui Penyedia dan Swakelola: 17 diagram, tiap kegiatan dengan pelaku, output, dan dasar hukumnya." />

      <section className="toolbar" aria-label="Pilih diagram">
        <div className="field-row" data-cols={projectsOn ? "3" : "2"}>
          <div className="field">
            <label htmlFor="pbj-diagram">Diagram</label>
            <select id="pbj-diagram" value={code} disabled={!flow.data} onChange={(e) => setQuery({ diagram: e.currentTarget.value, node: undefined })}>
              {flow.data ? null : <option value={code}>Memuat diagram</option>}
              {DIAGRAM_GROUPS.map((group) => {
                const items = summaries.filter((d) => d.group === group);
                if (items.length === 0) return null;
                return (
                  <optgroup key={group} label={DIAGRAM_GROUP_LABEL[group]}>
                    {items.map((d) => (
                      <option key={d.code} value={d.code}>
                        {d.title} ({d.mineCount} untuk {ROLE_LABEL[user.role]})
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>
          {projectsOn ? (
            <div className="field">
              <label htmlFor="pbj-flow-project">Project</label>
              <select id="pbj-flow-project" value={projectId ?? ""} disabled={pbjProjects.loading && !pbjProjects.data} onChange={(e) => setQuery({ project: e.currentTarget.value || undefined })}>
                <option value="">Tanpa project</option>
                {projectId && pbjProjects.data && !pbjProjects.data.some((p) => p.id === projectId) ? <option value={projectId}>{projectName ?? "Project tidak tersedia"}</option> : null}
                {(pbjProjects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="field pbj-toolbar-switch">
            <Switch
              id={switchId}
              size="sm"
              checked={mineOnly}
              onChange={(on) => setQuery({ mine: on ? "1" : undefined, node: undefined })}
              label={`Hanya aktivitas yang dapat dicentang peran saya (${ROLE_LABEL[user.role]})`}
            />
          </div>
        </div>
      </section>

      {flow.error ? (
        <Notice tone="danger" title="Daftar diagram belum termuat" action={<button type="button" className="btn btn-secondary" onClick={flow.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(flow.error)}</p>
        </Notice>
      ) : null}
      {project.error ? (
        <Notice tone="danger" title="Project belum termuat" action={<button type="button" className="btn btn-secondary" onClick={project.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(project.error)} Kegiatan yang sudah dicentang tidak ditandai.</p>
        </Notice>
      ) : null}

      {diagram.loading && !diagram.data ? (
        <SkeletonRows count={6} label="Memuat diagram" />
      ) : diagram.error ? (
        <Notice tone="danger" title="Diagram belum termuat" action={<button type="button" className="btn btn-secondary" onClick={diagram.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(diagram.error)}</p>
        </Notice>
      ) : diagram.data ? (
        <div className="pbj-alur">
          <section className="region" aria-labelledby="pbj-diagram-title">
            <SectionHead id="pbj-diagram-title" title={diagram.data.diagram.title} lead={diagram.data.diagram.goal} />
            {project.data ? (
              <p className="pbj-next pbj-flow-progress" role="status">
                {onRoute.length > 0 ? (
                  <>
                    <strong className="num">
                      {doneHere} dari {onRoute.length}
                    </strong>{" "}
                    kegiatan diagram ini selesai di <a href={projectPath(project.data.project.id)}>{project.data.project.name}</a>.
                  </>
                ) : (
                  <>
                    Diagram ini bukan bagian dari jalur <a href={projectPath(project.data.project.id)}>{project.data.project.name}</a>, jadi tidak ada kegiatan yang ditandai.
                  </>
                )}
              </p>
            ) : null}
            {shown.length === 0 ? (
              <EmptyState icon="filter" title={`Tidak ada kegiatan ${ROLE_LABEL[user.role]} di diagram ini`} body="Matikan filter peran untuk melihat seluruh proses." />
            ) : (
              <ol className="pbj-nodes" aria-label="Kegiatan diagram">
                {shown.map((n) => {
                  const done = activities.get(n.id)?.done ?? false;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        className="pbj-node"
                        aria-pressed={selected?.id === n.id}
                        data-done={done ? "" : undefined}
                        data-type={n.type}
                        onClick={() => setQuery({ node: n.id })}
                      >
                        <span className="pbj-node-num num" aria-hidden="true">
                          {done ? <Glyph name="check" size={16} /> : n.order}
                        </span>
                        <span className="pbj-node-text">
                          <span className="pbj-node-title">{n.activity}</span>
                          <span className="pbj-node-actor">{n.actor}</span>
                        </span>
                        <span className="pbj-node-marks">
                          {done ? <Badge tone="success">Selesai</Badge> : null}
                          {n.type === "keputusan" ? <Badge tone="warning">Keputusan</Badge> : null}
                          {n.mine ? <Badge tone="info">Peran Anda</Badge> : null}
                          {n.outside ? <Badge>Di luar Pluto</Badge> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
          {selected ? (
            <section className="region pbj-node-detail" aria-labelledby="pbj-node-title">
              <SectionHead
                id="pbj-node-title"
                title={selected.activity}
                lead={`${selected.type === "keputusan" ? "Titik keputusan" : (NODE_TYPE_LABEL[selected.type] ?? "Kegiatan")} ${selected.order} dari ${nodes.length}, simpul ${selected.id}`}
              />
              {selectedActivity?.done ? (
                <p className="pbj-next pbj-done-by">
                  <Glyph name="tickCircle" size={16} /> Dicentang oleh {selectedActivity.doneRole ? ROLE_LABEL[selectedActivity.doneRole] : "pelaku"}
                  {selectedActivity.doneAt ? ` pada ${formatDateTime(selectedActivity.doneAt)}` : ""} di {project.data?.project.name}.
                </p>
              ) : null}
              {selected.output && (canDraft(features) || canDocx(features)) ? (
                <div className="pbj-node-actions">
                  {canDraft(features) ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={draft.busy !== null}
                      aria-busy={draft.busy !== null || undefined}
                      aria-label={`Buat dokumen ${selected.output}`}
                      onClick={() => void draft.start(selected.id, () => API.draftNodeOutput(selected.id))}
                    >
                      <Glyph name="docAdd" size={16} />
                      <span>{draft.busy ? "Pluto menyusun draf" : "Buat dokumen"}</span>
                    </button>
                  ) : null}
                  {canDocx(features) ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={docx.busy !== null}
                      aria-busy={docx.busy !== null || undefined}
                      aria-label={`Unduh .docx ${selected.output}`}
                      onClick={() => void docx.start(selected.id, () => API.downloadNodeDocx(selected.id))}
                    >
                      <Glyph name="documentDownload" size={16} />
                      <span>{docx.busy ? "Menyiapkan" : "Unduh .docx"}</span>
                    </button>
                  ) : null}
                  {draft.error || docx.error ? (
                    <p className="pk-item-error" role="alert">
                      <Glyph name="danger" size={16} />
                      <span>{draft.error?.message ?? docx.error?.message}</span>
                    </p>
                  ) : null}
                </div>
              ) : null}
              <NodeFacts node={selected} nameOf={(id) => names.get(id)} />
              <p className="pbj-next pbj-node-note">
                <Glyph name="infoCircle" size={16} />
                {selected.outside
                  ? `Di luar Pluto. ${OUTSIDE_PLUTO_HINT}`
                  : selected.mine
                    ? `Kegiatan ini dicentang oleh ${ROLE_LABEL[user.role]} di project PBJ.`
                    : `Peran ${ROLE_LABEL[user.role]} hanya dapat melihat kegiatan ini.`}
              </p>
              {diagram.data.decisions.length > 0 ? (
                <div className="pbj-decisions">
                  <h3 className="pbj-subtitle">Titik keputusan</h3>
                  <ul className="rows">
                    {diagram.data.decisions.map((d) => (
                      <li key={d.id} className="row">
                        <span className="row-main">
                          <span className="row-title">{d.question}</span>
                          <span className="row-meta">
                            {d.owner}. {d.criteria}
                            {d.flow ? ` Alur: ${d.flow}` : ""}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      ) : null}

      <p className="pbj-footnote">
        Diagram adalah sintesis operasional, bukan prosedur resmi yang otomatis berlaku untuk setiap paket. Percabangan dan pengulangan proses mengikuti dokumen pemilihan, kontrak, dan regulasi yang relevan. Filter peran hanya menyaring tampilan, bukan pemberian hak akses.
      </p>
    </>
  );
}
